"use client";
import { useRouter } from "next/navigation";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTooltipFade } from "@/components/hooks/useTooltipFade.hook";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import CustomImage from "../shared/custom-image/custom-image";
import { Svg } from "image-config";
import useSignup from "@/hooks/auth/useSignup.hooks";
import AccountTypeModal from "./account-type-modal";

interface SignupFormProps {
  // No props needed - all logic is handled internally
}

export default function SignupForm({}: SignupFormProps) {
  const router = useRouter();

  const {
    form,
    accountType,
    setAccountType,
    submitting,
    error,
    clearError,
    // moved from component
    showPassword,
    togglePasswordVisibility,
    handleSubmit,
    showAccountTypeDialog,
    setShowAccountTypeDialog,
    handleGoogleSignup,
    handleAccountTypeConfirm,
    googleLoading,
  } = useSignup();

  const {
    showTooltip: showNameTooltip,
    handleMouseEnter: handleNameMouseEnter,
    handleMouseLeave: handleNameMouseLeave,
    handleTooltipOpenChange: handleNameTooltipOpenChange,
    hideTooltip: hideNameTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });

  const {
    showTooltip: showEmailTooltip,
    handleMouseEnter: handleEmailMouseEnter,
    handleMouseLeave: handleEmailMouseLeave,
    handleTooltipOpenChange: handleEmailTooltipOpenChange,
    hideTooltip: hideEmailTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });

  const {
    showTooltip: showPasswordTooltip,
    handleMouseEnter: handlePasswordMouseEnter,
    handleMouseLeave: handlePasswordMouseLeave,
    handleTooltipOpenChange: handlePasswordTooltipOpenChange,
    hideTooltip: hidePasswordTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });

  // submit and google handlers now provided by hook

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        data-testid="signup-form"
      >
        {/* Error Message */}
        {error && (
          <div
            className="text-red-600 p-3 bg-red-50 rounded-lg text-sm"
            data-testid="signup-form-error"
          >
            {error}
          </div>
        )}

        {/* Account Type Selection */}
        <div className="space-y-3">
          <Tabs
            value={accountType}
            onValueChange={(value) => {
              setAccountType(value as "homeowner" | "contractor");
              clearError(); // Clear error when user changes account type
            }}
            className="w-full"
            data-testid="signup-account-type-tabs"
          >
            <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg h-auto">
              <TabsTrigger
                value="homeowner"
                className="rounded-md py-2 px-4 data-[state=active]:bg-white cursor-pointer data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                data-testid="signup-account-type-homeowner"
              >
                I&apos;m a homeowner
              </TabsTrigger>
              <TabsTrigger
                value="contractor"
                className="rounded-md py-2 px-4 data-[state=active]:bg-white cursor-pointer data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-600"
                data-testid="signup-account-type-contractor"
              >
                I&apos;m a contractor
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Name Field */}
        <div className="space-y-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Name</FormLabel>
                <FormControl>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip
                      open={showNameTooltip}
                      onOpenChange={handleNameTooltipOpenChange}
                    >
                      <TooltipTrigger asChild>
                        <Input
                          placeholder="Name"
                          className={cn(
                            "h-[42px]",
                            form.formState.errors.name &&
                              "border-red-500 focus-visible:ring-red-500"
                          )}
                          {...field}
                          onMouseEnter={() =>
                            handleNameMouseEnter(field.value || "")
                          }
                          onMouseLeave={handleNameMouseLeave}
                          onChange={(e) => {
                            field.onChange(e);
                            clearError(); // Clear error when user starts typing
                            // Hide tooltip immediately when user starts typing
                            hideNameTooltip();
                          }}
                          data-testid="signup-input-name"
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="end"
                        className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                        data-testid="signup-name-tooltip"
                      >
                        <p className="text-xs leading-2">
                          Please fill out this field
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Email</FormLabel>
                <FormControl>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip
                      open={showEmailTooltip}
                      onOpenChange={handleEmailTooltipOpenChange}
                    >
                      <TooltipTrigger asChild>
                        <Input
                          type="email"
                          placeholder="Email"
                          className={cn(
                            "h-[42px]",
                            form.formState.errors.email &&
                              "border-red-500 focus-visible:ring-red-500"
                          )}
                          {...field}
                          onMouseEnter={() =>
                            handleEmailMouseEnter(field.value || "")
                          }
                          onMouseLeave={handleEmailMouseLeave}
                          onChange={(e) => {
                            field.onChange(e);
                            clearError(); // Clear error when user starts typing
                            // Hide tooltip immediately when user starts typing
                            hideEmailTooltip();
                          }}
                          data-testid="signup-input-email"
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="end"
                        className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                        data-testid="signup-email-tooltip"
                      >
                        <p className="text-xs leading-2">
                          Please fill out this field
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Mobile Phone Field — contractors only (receives the SMS code) */}
        {accountType === "contractor" && (
          <div className="space-y-2">
            <FormField
              control={form.control}
              name="mobilePhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">Mobile Phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="Mobile Phone (e.g. +1 416 555 0123)"
                      className={cn(
                        "h-[42px]",
                        form.formState.errors.mobilePhone &&
                          "border-red-500 focus-visible:ring-red-500"
                      )}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        clearError();
                      }}
                      data-testid="signup-input-mobile-phone"
                    />
                  </FormControl>
                  <p className="text-xs text-gray-500 text-left">
                    We&apos;ll text a verification code to this number.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Password Field */}
        <div className="space-y-2">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Password</FormLabel>
                <FormControl>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip
                      open={showPasswordTooltip}
                      onOpenChange={handlePasswordTooltipOpenChange}
                    >
                      <TooltipTrigger asChild>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            className={cn(
                              "h-[42px] pr-10",
                              form.formState.errors.password &&
                                "border-red-500 focus-visible:ring-red-500"
                            )}
                            {...field}
                            onMouseEnter={() =>
                              handlePasswordMouseEnter(field.value || "")
                            }
                            onMouseLeave={handlePasswordMouseLeave}
                            onChange={(e) => {
                              field.onChange(e);
                              clearError(); // Clear error when user starts typing
                              // Hide tooltip immediately when user starts typing
                              hidePasswordTooltip();
                            }}
                            data-testid="signup-input-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-[26px] w-[26px] p-0 flex items-center justify-center !rounded-lg"
                            onClick={togglePasswordVisibility}
                            data-testid="signup-toggle-password"
                          >
                            {showPassword ? (
                              <EyeOff className="h-[17px] w-[17px] text-input" />
                            ) : (
                              <Eye className="h-[17px] w-[17px] text-input" />
                            )}
                            <span className="sr-only">
                              {showPassword ? "Hide password" : "Show password"}
                            </span>
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="end"
                        className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                        data-testid="signup-password-tooltip"
                      >
                        <p className="text-xs leading-2">
                          Please fill out this field
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Sign Up Button */}
        <Button
          type="submit"
          className="w-full h-[42px]"
          disabled={submitting}
          data-testid="signup-submit-button"
        >
          {submitting ? "Signing up..." : "Sign up"}
        </Button>

        {/* Divider */}
        <div className="relative sm:mt-2 mt-0" data-testid="signup-divider">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-input" />
          </div>
          <div className="relative flex justify-center  text-xs uppercase">
            <span className="bg-white px-2  font-medium">OR</span>
          </div>
        </div>

        {/* Google Sign Up */}
        <Button
          type="button"
          variant="outline"
          className="w-full h-[42px] bg-transparent sm:mt-2 mt-0"
          onClick={handleGoogleSignup}
          disabled={googleLoading}
          data-testid="signup-google-button"
        >
          <CustomImage
            src={Svg.Google}
            alt="Google"
            width={28}
            height={28}
            className="mr-2"
          />
          {googleLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Redirecting to Google...
            </div>
          ) : (
            "Sign up with Google"
          )}
        </Button>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-600 sm:mt-3 mt-0">
          Already have an account?{" "}
          <a
            href="/login"
            className="text-[oklch(0.67_0.15_238.95)] hover:underline cursor-pointer"
            data-testid="signup-login-link"
          >
            Login
          </a>
        </p>
      </form>

      <AccountTypeModal
        open={showAccountTypeDialog}
        onOpenChange={setShowAccountTypeDialog}
        onContinue={handleAccountTypeConfirm}
      />
    </Form>
  );
}
